import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { danaClientContext } from './install/danaClientContext';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { X, Send, Loader2, ArrowUpRight, Mic, Square, Check, Phone, PhoneOff, Plus } from 'lucide-react';
import { aiChat, getDanaConversation, deleteDanaConversation, type AiChatTurn, type AiChatContext, getCachedProfile } from '@/lib/hrApi';
import {
  voiceAvailability, voiceTurn, confirmVoiceAction, cancelVoiceAction,
  type VoiceProposal,
} from '@/lib/danaVoiceApi';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { useDanaCall } from './useDanaCall';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useAppSelector } from '@/app/hooks';
import { selectUser } from '@/features/auth/state/auth-state/authSlice';
import { deriveContext, quickActionsFor } from '@/lib/aiContext';
import { getApiErrorMessage } from '@/lib/apiError';
import { DanaGreeting } from './DanaGreeting';
import { DanaAvatar } from './branding/DanaAvatar';

interface Msg extends AiChatTurn { id: number; proposals?: VoiceProposal[]; }

/** Render **bold** spans inline (lightweight — no markdown dependency). */
function renderInline(text: string): React.ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(p);
    return m ? <strong key={i}>{m[1]}</strong> : <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

/** Conversational renderer for assistant replies: paragraphs + bullet lines +
 *  inline bold. Avoids a full markdown lib; the assistant is prompted not to use
 *  tables/headings, so this covers what it actually produces. */
const RichText: React.FC<{ text: string }> = ({ text }) => (
  <div className="space-y-1.5">
    {text.split('\n').map((line, i) => {
      const t = line.trim();
      if (t === '') return <div key={i} className="h-1" />;
      const bullet = /^[-*•]\s+(.*)/.exec(t);
      if (bullet) {
        return (
          <div key={i} className="flex gap-1.5">
            <span className="select-none text-orange-400">•</span>
            <span>{renderInline(bullet[1])}</span>
          </div>
        );
      }
      return <div key={i}>{renderInline(t)}</div>;
    })}
  </div>
);

interface AssistantPanelProps {
  /** The current user's resolved, RBAC/entitlement-filtered navigation — passed
   *  so Dana always knows the exact screens THIS user can reach (no drift). */
  navPages?: { label: string; path: string; group: string }[];
}

export const AssistantPanel: React.FC<AssistantPanelProps> = ({ navPages }) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [pendingAsk, setPendingAsk] = useState<string | null>(null);
  // null = no nudge. {} = generic nudge. {text, ask} = contextual (e.g. empty state).
  const [nudge, setNudge] = useState<{ text?: string; ask?: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // --- Dana Voice (push-to-talk pilot; entitlement-gated, fail-closed server-side)
  const { hasProductStrict } = useEntitlements();
  const voiceEntitled = hasProductStrict('dana_voice');
  const [voiceReady, setVoiceReady] = useState(false);
  const [realtimeReady, setRealtimeReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const vadRef = useRef<{ ctx: AudioContext; timer: number } | null>(null);
  // Conversation mode: tap the mic ONCE to start a hands-free loop —
  // speak → auto-send on silence → Dana answers aloud → mic reopens.
  // Tap again (or close the panel) to end it. Auto-ends after a silent turn.
  const convoRef = useRef(false);
  const [convo, setConvo] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    if (!open || !voiceEntitled) return;
    voiceAvailability().then((a: any) => {
      setVoiceReady(!!a.enabled);
      // Server says realtime exists — but the BROWSER must be able to do
      // WebRTC + mic too. Proxy browsers (Opera Mini, some data-saver modes)
      // can't; hide the call button there instead of offering a dead end.
      // (Push-to-talk needs MediaRecorder, gated separately via voiceReady use.)
      const webrtcCapable =
        typeof RTCPeerConnection !== 'undefined' &&
        typeof navigator !== 'undefined' &&
        !!navigator.mediaDevices?.getUserMedia;
      setRealtimeReady(!!a.realtime && webrtcCapable);
    }).catch(() => { setVoiceReady(false); setRealtimeReady(false); });
  }, [open, voiceEntitled]);
  const nextId = useRef(1);
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const { isTenantAdmin, isManager } = useRoleAccess();

  const role: 'admin' | 'manager' | 'employee' = isTenantAdmin ? 'admin' : isManager ? 'manager' : 'employee';
  const authUser = useAppSelector(selectUser);

  // --- Conversation history (Option 1 localStorage cache + Option 2 server store).
  // The widget keeps a per-user local cache for an instant restore on reload, and
  // reconciles with the server (source of truth, retention-enforced) on mount.
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const chatKey = (authUser as any)?.id ? `dana_chat:${(authUser as any).id}` : '';
  const restoredRef = useRef(false);
  const CLIENT_RETENTION_MS = 7 * 24 * 60 * 60 * 1000; // client-side stale cutoff (mirrors default)

  useEffect(() => {
    if (restoredRef.current || !chatKey) return;
    restoredRef.current = true;
    // 1) Instant paint from localStorage (dropped if older than the retention window).
    try {
      const raw = localStorage.getItem(chatKey);
      if (raw) {
        const saved = JSON.parse(raw);
        const fresh = !saved?.savedAt || Date.now() - saved.savedAt < CLIENT_RETENTION_MS;
        if (fresh && Array.isArray(saved?.messages) && saved.messages.length) {
          setMessages(saved.messages.map((m: any, i: number) => ({ id: i + 1, role: m.role, content: m.content })));
          nextId.current = saved.messages.length + 1;
          if (saved?.conversationId) setConversationId(saved.conversationId);
        } else if (!fresh) {
          localStorage.removeItem(chatKey);
        }
      }
    } catch { /* storage unavailable */ }
    // 2) Reconcile with the server (authoritative + retention-filtered). Server wins
    // when it has data; if it has none we keep the local cache as a fallback.
    getDanaConversation().then((conv) => {
      if (conv?.conversation_id && conv.messages?.length) {
        setConversationId(conv.conversation_id);
        setMessages(conv.messages.map((m, i) => ({ id: i + 1, role: m.role, content: m.content })));
        nextId.current = conv.messages.length + 1;
      }
    }).catch(() => { /* offline — keep local */ });
  }, [chatKey]);

  // Persist the thread to localStorage (capped) whenever it changes.
  useEffect(() => {
    if (!chatKey) return;
    try {
      const toSave = messages.slice(-40).map((m) => ({ role: m.role, content: m.content }));
      if (toSave.length) localStorage.setItem(chatKey, JSON.stringify({ conversationId, messages: toSave, savedAt: Date.now() }));
      else localStorage.removeItem(chatKey);
    } catch { /* ignore */ }
  }, [messages, conversationId, chatKey]);

  const newChat = () => {
    const prev = conversationId;
    setMessages([]);
    setConversationId(undefined);
    nextId.current = 1;
    try { if (chatKey) localStorage.removeItem(chatKey); } catch { /* ignore */ }
    if (prev) deleteDanaConversation(prev);
  };
  // Reliable first name: cached profile → Redux user → localStorage fallback.
  const firstName =
    getCachedProfile()?.first_name ||
    (authUser as any)?.firstName ||
    ((authUser as any)?.name || localStorage.getItem('userName') || '').trim().split(' ')[0] ||
    undefined;
  const ctxNow = deriveContext(location.pathname);
  const actions = quickActionsFor(ctxNow.page, role);
  const appPath = (p: string) => (tenantSlug ? `/t/${tenantSlug}/app/${p}` : `/app/${p}`);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  // Open Dana from anywhere (e.g. the Help page) via a window event, optionally
  // with a question to ask straight away.
  useEffect(() => {
    const handler = (e: Event) => {
      const q = (e as CustomEvent).detail?.question as string | undefined;
      setOpen(true);
      if (q && q.trim()) setPendingAsk(q.trim());
    };
    window.addEventListener('dana:ask', handler as EventListener);
    return () => window.removeEventListener('dana:ask', handler as EventListener);
  }, []);

  useEffect(() => {
    if (open && pendingAsk) {
      const q = pendingAsk;
      setPendingAsk(null);
      void send(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingAsk]);

  // Greet once per session: auto-open Dana the first time after login so the
  // user notices the assistant (the greeting is context-aware). Marked in
  // sessionStorage so we never reopen on every navigation.
  useEffect(() => {
    if (sessionStorage.getItem('dana_greeted')) return;
    const id = setTimeout(() => {
      setOpen(true);
      sessionStorage.setItem('dana_greeted', '1');
    }, 1200);
    return () => clearTimeout(id);
  }, []);

  // After the first greeting, give a gentle nudge bubble when the user lands on
  // a new page (closed only) — noticeable but non-blocking. Stops once dismissed
  // for the session.
  useEffect(() => {
    if (open || !sessionStorage.getItem('dana_greeted') || sessionStorage.getItem('dana_nudge_off')) {
      setNudge(null);
      return;
    }
    setNudge({});
    const id = setTimeout(() => setNudge(null), 7000);
    return () => clearTimeout(id);
  }, [location.pathname, open]);

  // Contextual nudge: empty states (e.g. an empty Payroll Runs page) fire
  // `dana:nudge` with a tailored line + an optional question to ask on click.
  useEffect(() => {
    const handler = (e: Event) => {
      if (open || sessionStorage.getItem('dana_nudge_off')) return;
      const d = (e as CustomEvent).detail || {};
      setNudge({ text: d.message, ask: d.question });
      const id = window.setTimeout(() => setNudge(null), 12000);
      return () => window.clearTimeout(id);
    };
    window.addEventListener('dana:nudge', handler as EventListener);
    return () => window.removeEventListener('dana:nudge', handler as EventListener);
  }, [open]);

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || sending) return;
    const userMsg: Msg = { id: nextId.current++, role: 'user', content: q };
    const history: AiChatTurn[] = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);
    try {
      const ctx: AiChatContext = {
        ...deriveContext(location.pathname), role, navigation: navPages, client: danaClientContext(),
      };
      const res = await aiChat(q, history, ctx, conversationId);
      if (res.conversation_id) setConversationId(res.conversation_id);
      setMessages((m) => [...m, { id: nextId.current++, role: 'assistant', content: res.reply }]);
    } catch (e: any) {
      const msg = e?.isSessionExpired
        ? t('dana.error_session_expired')
        : getApiErrorMessage(e, t('dana.error_generic'));
      setMessages((m) => [...m, { id: nextId.current++, role: 'assistant', content: msg }]);
    } finally {
      setSending(false);
    }
  };

  const call = useDanaCall((p) => {
    setMessages((m) => [...m, {
      id: nextId.current++, role: 'assistant',
      content: t('dana.review_action', { defaultValue: 'Review before I do this' }),
      proposals: [p],
    }]);
  });

  const stopRecorder = () => {
    if (vadRef.current) {
      window.clearInterval(vadRef.current.timer);
      void vadRef.current.ctx.close().catch(() => {});
      vadRef.current = null;
    }
    recorderRef.current?.stop();
    recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    recorderRef.current = null;
    setRecording(false);
  };

  // Dana speaks her reply for VOICE turns (browser TTS — no dependency, no
  // audio leaves the machine). Interrupting = start talking; we cancel speech
  // the moment the mic opens.
  const onDanaFinishedSpeaking = () => {
    // Continue the conversation: reopen the mic for the next turn.
    if (convoRef.current) void startVoice(true);
  };

  const speakReply = (text: string, lang?: string, audioB64?: string | null) => {
    try {
      window.speechSynthesis?.cancel();
      audioRef.current?.pause();
      if (audioB64) {
        // Natural server voice (one round trip; nothing retained).
        const audio = new Audio(`data:audio/mpeg;base64,${audioB64}`);
        audioRef.current = audio;
        audio.onended = onDanaFinishedSpeaking;
        audio.onerror = onDanaFinishedSpeaking;
        void audio.play().catch(() => onDanaFinishedSpeaking());
        return;
      }
      if (!('speechSynthesis' in window) || !text) { onDanaFinishedSpeaking(); return; }
      const clean = text.replace(/[*_#`]/g, '').replace(/\s+/g, ' ').trim().slice(0, 600);
      const u = new SpeechSynthesisUtterance(clean);
      u.lang = lang === 'sw' ? 'sw' : 'en-US';
      u.rate = 1.02;
      u.onend = onDanaFinishedSpeaking;
      window.speechSynthesis.speak(u);
    } catch { onDanaFinishedSpeaking(); }
  };

  const startVoice = async (fromLoop = false) => {
    if (!fromLoop) {
      if (recording || convoRef.current) {
        // second tap = end the conversation
        convoRef.current = false;
        setConvo(false);
        stopRecorder();
        try { window.speechSynthesis?.cancel(); } catch { /* ok */ }
        audioRef.current?.pause();
        return;
      }
      convoRef.current = true;
      setConvo(true);
    } else if (!convoRef.current) {
      return; // loop was ended while Dana spoke
    }
    try {
      try { window.speechSynthesis?.cancel(); } catch { /* barge-in: stop Dana talking */ }
      audioRef.current?.pause();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        chunksRef.current = [];
        if (blob.size < 1000) { convoRef.current = false; setConvo(false); return; } // silence/accidental tap
        setTranscribing(true);
        try {
          const res = await voiceTurn(blob, blob.type);
          speakReply(res.reply, res.transcript?.language, res.reply_audio_b64);
          if (res.transcript?.text) {
            setMessages((m) => [...m, { id: nextId.current++, role: 'user', content: res.transcript.text }]);
          }
          setMessages((m) => [...m, {
            id: nextId.current++, role: 'assistant', content: res.reply,
            proposals: res.proposals?.length ? res.proposals : undefined,
          }]);
        } catch (e: any) {
          setMessages((m) => [...m, {
            id: nextId.current++, role: 'assistant',
            content: getApiErrorMessage(e, t('dana.error_generic')),
          }]);
        } finally {
          setTranscribing(false);
        }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
      // hard 60s cap, mirroring the server limit
      setTimeout(() => { if (recorderRef.current === rec && rec.state === 'recording') stopRecorder(); }, 60000);
      // Voice-activity auto-send: after ~1.4s of silence (and at least 1s of
      // speech) the clip sends itself — no need to tap stop. Tap still works.
      try {
        const ctx = new AudioContext();
        const src = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 2048;
        src.connect(analyser);
        const buf = new Uint8Array(analyser.fftSize);
        const startedAt = Date.now();
        let lastLoud = Date.now();
        let heardSpeech = false;
        const timer = window.setInterval(() => {
          if (recorderRef.current !== rec) return;
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) { const d = (buf[i] - 128) / 128; sum += d * d; }
          const rms = Math.sqrt(sum / buf.length);
          if (rms > 0.02) { lastLoud = Date.now(); heardSpeech = true; }
          const silentFor = Date.now() - lastLoud;
          if (heardSpeech && Date.now() - startedAt > 1000 && silentFor > 1400) stopRecorder();
          // nothing said for 8s → the user is done; end the conversation quietly
          if (!heardSpeech && Date.now() - startedAt > 8000) {
            convoRef.current = false;
            setConvo(false);
            stopRecorder();
          }
        }, 200);
        vadRef.current = { ctx, timer };
      } catch { /* no VAD — manual stop still works */ }
    } catch {
      convoRef.current = false;
      setConvo(false);
      setMessages((m) => [...m, {
        id: nextId.current++, role: 'assistant',
        content: t('dana.mic_denied', { defaultValue: 'I need microphone access for voice — check your browser permission and try again.' }),
      }]);
    }
  };

  const resolveProposal = async (msgId: number, p: VoiceProposal, ok: boolean) => {
    try {
      const done = ok
        ? await confirmVoiceAction(p.action_id, p.payload_sha256)
        : await cancelVoiceAction(p.action_id);
      setMessages((m) => m.map((msg) => msg.id === msgId
        ? { ...msg, proposals: msg.proposals?.filter((x) => x.action_id !== p.action_id) }
        : msg));
      setMessages((m) => [...m, {
        id: nextId.current++, role: 'assistant',
        content: ok
          ? (done.status === 'executed'
              ? t('dana.action_done', { defaultValue: 'Done — that\u2019s taken care of.' })
              : t('dana.action_pending', { defaultValue: 'Working on it\u2026' }))
          : t('dana.action_canceled', { defaultValue: 'Okay, I won\u2019t do that.' }),
      }]);
    } catch (e: any) {
      setMessages((m) => [...m, {
        id: nextId.current++, role: 'assistant',
        content: getApiErrorMessage(e, t('dana.error_generic')),
      }]);
    }
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-2">
          {/* Gentle nudge bubble — noticeable but non-blocking */}
          {nudge && (
            <div className="relative mr-1 max-w-[250px] rounded-2xl rounded-br-sm border border-border bg-card px-3 py-2 text-sm text-foreground shadow-lg animate-in fade-in slide-in-from-bottom-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setNudge(null); sessionStorage.setItem('dana_nudge_off', '1'); }}
                aria-label={t('dana.dismiss_aria', { defaultValue: 'Dismiss' })}
                className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => { setOpen(true); if (nudge.ask) setPendingAsk(nudge.ask); setNudge(null); }}
                className="text-left"
              >
                {nudge.text ? (
                  <span><span className="font-medium">Dana:</span> {nudge.text}</span>
                ) : (
                  <span>
                    <span className="font-medium">{firstName ? `Hi ${firstName}! 👋` : 'Hi! 👋'}</span>{' '}
                    {t('dana.nudge', { defaultValue: "I'm Dana — need a hand? Tap to chat." })}
                  </span>
                )}
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={() => { setOpen(true); setNudge(false); }}
            aria-label={t('dana.open_aria')}
            className="relative h-14 w-14 rounded-full shadow-lg hover:scale-105 transition-transform"
          >
            {/* Attention pulse so Dana is easy to spot */}
            <span className="absolute inset-0 rounded-full bg-orange-400/40 animate-ping" aria-hidden />
            <DanaAvatar size={56} ring />
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[min(92vw,400px)] h-[min(80vh,560px)] bg-card rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white">
            <div className="flex items-center gap-2">
              <DanaAvatar size={32} ring />
              <div className="leading-tight">
                <div className="font-bold">Dana</div>
                <div className="text-[11px] text-white/85 -mt-0.5">{t('dana.subtitle')}</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {messages.length > 0 && (
                <button
                  type="button"
                  onClick={newChat}
                  aria-label={t('dana.new_chat_aria', { defaultValue: 'Start a new chat (clears this conversation)' })}
                  title={t('dana.new_chat_aria', { defaultValue: 'Start a new chat (clears this conversation)' })}
                  className="flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs font-medium hover:bg-white/25"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {t('dana.new_chat', { defaultValue: 'New chat' })}
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)} aria-label={t('dana.close_aria')} className="hover:bg-white/20 rounded p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted">
            {messages.length === 0 && (
              <div className="space-y-3">
                {/* Dana speaks first — just a context-aware welcome. No canned
                    suggestions: they could imply access a given user doesn't have. */}
                <DanaGreeting page={ctxNow.page} role={role} name={firstName} />
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                  m.role === 'user' ? 'whitespace-pre-wrap bg-orange-500 text-white' : 'bg-card border border-border text-foreground'
                }`}>
                  {m.role === 'user' ? m.content : <RichText text={m.content} />}
                  {/* Server-owned action proposals: nothing runs until Confirm. */}
                  {m.proposals?.map((p) => (
                    <div key={p.action_id} className="mt-2 rounded-lg border border-orange-300 dark:border-orange-500/40 p-2.5 space-y-1.5">
                      <div className="text-xs font-semibold text-orange-700 dark:text-orange-300">
                        {t('dana.review_action', { defaultValue: 'Review before I do this' })}
                      </div>
                      <div className="text-sm">{p.summary}</div>
                      {['start_at', 'end_at', 'scheduled_at', 'start_date', 'end_date', 'location'].map((k) =>
                        p.payload?.[k] ? (
                          <div key={k} className="text-xs text-muted-foreground">{k.replace(/_/g, ' ')}: {String(p.payload[k])}</div>
                        ) : null,
                      )}
                      <div className="flex gap-2 pt-1">
                        <button type="button" onClick={() => resolveProposal(m.id, p, true)}
                          className="inline-flex items-center gap-1 rounded-md bg-orange-500 px-2.5 py-1 text-xs font-semibold text-white">
                          <Check className="h-3 w-3" /> {t('dana.confirm', { defaultValue: 'Confirm' })}
                        </button>
                        <button type="button" onClick={() => resolveProposal(m.id, p, false)}
                          className="rounded-md border border-border px-2.5 py-1 text-xs">
                          {t('dana.cancel', { defaultValue: 'Cancel' })}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-3 py-2 bg-card border border-border text-muted-foreground flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t('dana.thinking')}
                </div>
              </div>
            )}
          </div>

          {/* Context-aware deep links — one click to the right place. */}
          {actions.length > 0 && (
            <div className="px-3 pt-2 flex flex-wrap gap-1.5">
              {actions.map((a) => (
                <button
                  key={a.to + a.label}
                  type="button"
                  onClick={() => { setOpen(false); navigate(appPath(a.to)); }}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-orange-200 dark:border-orange-500/30 text-orange-700 dark:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-500/10"
                >
                  {a.label} <ArrowUpRight className="w-3 h-3" />
                </button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="p-3 border-t border-border flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('dana.input_placeholder')}
              className="flex-1 h-10 px-3 rounded-lg border border-border focus:border-orange-400 focus:outline-none text-sm"
            />
            {voiceEntitled && realtimeReady && (
              <button
                type="button"
                onClick={() => { void call.startCall().catch(() => {
                  setMessages((m) => [...m, { id: nextId.current++, role: 'assistant',
                    content: t('dana.call_failed', { defaultValue: 'I couldn\u2019t start the call — try again in a moment.' }) }]);
                }); }}
                aria-label={call.state === 'idle'
                  ? t('dana.start_call', { defaultValue: 'Start a live call with Dana' })
                  : t('dana.end_call', { defaultValue: 'End the call' })}
                className={`h-10 w-10 rounded-lg flex items-center justify-center border ${
                  call.state === 'live'
                    ? call.danaSpeaking
                      ? 'bg-green-500 text-white border-green-500'
                      : 'bg-green-600 text-white border-green-600 animate-pulse'
                    : call.state === 'connecting' || call.state === 'reconnecting'
                      ? 'bg-amber-500 text-white border-amber-500 animate-pulse'
                      : 'border-border text-green-600 hover:bg-green-50 dark:hover:bg-green-500/10'
                }`}
                title={call.state === 'live' ? (call.danaSpeaking ? 'Dana is speaking' : 'Listening — just talk') : undefined}
              >
                {call.state === 'idle' || call.state === 'error'
                  ? <Phone className="w-4 h-4" />
                  : call.state === 'connecting' || call.state === 'reconnecting'
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <PhoneOff className="w-4 h-4" />}
              </button>
            )}
            {/* Push-to-talk is the fallback voice UI — hidden whenever the live call is available */}
            {voiceEntitled && voiceReady && !realtimeReady && call.state === 'idle' && (
              <button
                type="button"
                onClick={() => startVoice()}
                disabled={transcribing}
                aria-label={convo
                  ? t('dana.end_conversation', { defaultValue: 'End voice conversation' })
                  : t('dana.talk', { defaultValue: 'Talk to Dana' })}
                className={`h-10 w-10 rounded-lg flex items-center justify-center border ${
                  recording
                    ? 'bg-red-500 text-white border-red-500 animate-pulse'
                    : convo
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'border-border text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-500/10'
                } disabled:opacity-50`}
              >
                {transcribing
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : recording || convo ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            <button type="submit" disabled={sending || !input.trim()} aria-label={t('dana.send')}
              className="h-10 w-10 rounded-lg bg-orange-500 text-white flex items-center justify-center disabled:opacity-50">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AssistantPanel;
